/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Composes state, effects, and handlers for the ViewAppointments page.
 * @module pages/viewAppointments/useViewAppointments
 */

import { useMemo } from "react";
import useViewAppointmentsContext from "./useViewAppointmentsContext.js";
import useViewAppointmentsFetching from "./useViewAppointmentsFetching.js";
import useViewAppointmentsEffects from "./useViewAppointmentsEffects.js";
import useViewAppointmentsFilterHandlers from "./useViewAppointmentsFilterHandlers.js";
import useViewAppointmentsConfirmHandlers from "./useViewAppointmentsConfirmHandlers.js";
import useViewAppointmentsVirtualHandlers from "./useViewAppointmentsVirtualHandlers.js";
import useViewAppointmentsNavigationHandlers from "./useViewAppointmentsNavigationHandlers.js";
import { buildPrimarySelectStyles } from "./viewAppointmentsSelectStyles.js";
import { buildViewAppointmentsViewModel } from "./buildViewAppointmentsViewModel.js";

/**
 * View-model hook for the ViewAppointments page.
 * @returns {Object} Grouped props for `ViewAppointmentsView`
 */
const useViewAppointments = () => {
  const ctx = useViewAppointmentsContext();
  const fetching = useViewAppointmentsFetching(ctx);
  useViewAppointmentsEffects(ctx, fetching);

  const filterHandlers = useViewAppointmentsFilterHandlers(ctx, fetching);
  const confirmHandlers = useViewAppointmentsConfirmHandlers(ctx, fetching);
  const virtualHandlers = useViewAppointmentsVirtualHandlers(ctx);
  const navigationHandlers = useViewAppointmentsNavigationHandlers(
    ctx,
    fetching,
    virtualHandlers,
    filterHandlers,
  );

  const primarySelectStyles = useMemo(
    () => buildPrimarySelectStyles(),
    [],
  );
  const executiveSelectStyles = useMemo(
    () =>
      buildPrimarySelectStyles({ disabled: !ctx.selectedBuyerCompany }),
    [ctx.selectedBuyerCompany],
  );
  const requirementSelectStyles = useMemo(
    () =>
      buildPrimarySelectStyles({
        disabled:
          ctx.userData?.tipoDeUsuario === "5" && !ctx.selectedBuyerExecutive,
      }),
    [ctx.userData, ctx.selectedBuyerExecutive],
  );

  const handlers = {
    ...filterHandlers,
    ...confirmHandlers,
    ...virtualHandlers,
    ...navigationHandlers,
  };

  return buildViewAppointmentsViewModel(
    ctx,
    fetching,
    { primarySelectStyles, executiveSelectStyles, requirementSelectStyles },
    handlers,
  );
};

export default useViewAppointments;
